// src/User/MisCertificados.js — Solponiente
// Formulario para solicitar un certificado + listado del estado de las
// solicitudes de certificado ya presentadas por el usuario.
import React, { useEffect, useState } from "react";
import { Card, Table, Spinner, Badge, Form, Button, Container } from "react-bootstrap";
import Header from "../Components/Header";
import "bootstrap-icons/font/bootstrap-icons.css";

const BASE_URL = "https://oficinavirtual-solponiente.com/api";

// Mismo mapa que Admin/GestionCertificados.js. Con un solo tipo por ahora,
// pero pensado para añadir más sin tocar el esquema de datos.
const TIPOS_CERTIFICADO = {
  al_corriente_pago: "Certificado de estar al corriente de pago",
};

const MisCertificados = () => {
  const [certificados, setCertificados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tipoSeleccionado, setTipoSeleccionado] = useState("al_corriente_pago");
  const [motivo, setMotivo] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [descargando, setDescargando] = useState(null);
  const userId = localStorage.getItem("id");
  const token = localStorage.getItem("token");

  const fetchCertificados = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/solicitudes/usuarios/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setCertificados(data.filter((s) => s.TipoSolicitud === "certificado"));
      } else {
        setCertificados([]);
      }
    } catch (e) {
      console.error("Error al obtener los certificados:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) fetchCertificados();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const solicitarCertificado = async (e) => {
    e.preventDefault();
    setEnviando(true);
    try {
      const now = new Date();
      const fechaHora = now.toISOString().split("T").join(" ").split("Z")[0];
      const res = await fetch(`${BASE_URL}/solicitudes`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          UsuarioID: userId,
          Estado: "pendiente",
          TipoSolicitud: "certificado",
          TipoCertificado: tipoSeleccionado,
          Descripcion: motivo,
          FechaSolicitud: fechaHora,
        }),
      });
      if (res.ok) {
        setMotivo("");
        alert("Su solicitud de certificado ha sido registrada.");
        fetchCertificados();
      } else {
        alert("Error al registrar la solicitud de certificado.");
      }
    } catch (e) {
      alert("No se pudo conectar con el servidor.");
    } finally {
      setEnviando(false);
    }
  };

  const descargar = async (documentoId, nombre) => {
    setDescargando(documentoId);
    try {
      const res = await fetch(`${BASE_URL}/documentos/${documentoId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = nombre || "certificado.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      alert("No se pudo descargar el certificado.");
    } finally {
      setDescargando(null);
    }
  };

  const renderEstado = (cert) => {
    if (cert.FirmadoAdmin) return <Badge bg="success">Enviado</Badge>;
    if (cert.DocumentoID) return <Badge bg="warning" text="dark">En preparación</Badge>;
    return <Badge bg="secondary">Solicitado</Badge>;
  };

  return (
    <div className="d-flex flex-column">
      <Header />
      <main className="flex-grow-1">
        <Container fluid className="py-3 px-4">
          <h4 className="mb-2">
            <i className="bi bi-patch-check text-primary me-2"></i>
            Mis Certificados
          </h4>
          <p className="text-muted mb-4">
            Solicite un certificado y consulte el estado de sus solicitudes.
            Una vez firmado por la administración, podrá descargarlo aquí.
          </p>

          <Card className="shadow-sm bg-white mb-4" style={{ borderRadius: "0.75rem" }}>
            <Card.Body>
              <h5 className="mb-3">
                <i className="bi bi-file-earmark-plus text-primary me-2"></i>
                Solicitar un certificado
              </h5>
              <Form onSubmit={solicitarCertificado}>
                <Form.Group className="mb-3" controlId="tipoCertificado" style={{ maxWidth: 500 }}>
                  <Form.Label>Tipo de certificado</Form.Label>
                  <Form.Select
                    value={tipoSeleccionado}
                    onChange={(e) => setTipoSeleccionado(e.target.value)}
                  >
                    {Object.entries(TIPOS_CERTIFICADO).map(([valor, etiqueta]) => (
                      <option key={valor} value={valor}>{etiqueta}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3" controlId="motivoCertificado" style={{ maxWidth: 500 }}>
                  <Form.Label>Observaciones (opcional)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="Indique cualquier detalle adicional para su solicitud…"
                    value={motivo}
                    onChange={(e) => setMotivo(e.target.value)}
                  />
                </Form.Group>
                <Button type="submit" variant="primary" disabled={enviando}>
                  {enviando ? (
                    <><Spinner animation="border" size="sm" className="me-2" />Enviando…</>
                  ) : (
                    <><i className="bi bi-send me-1"></i>Solicitar certificado</>
                  )}
                </Button>
              </Form>
            </Card.Body>
          </Card>

          <Card className="shadow-sm bg-white" style={{ borderRadius: "0.75rem" }}>
            <Card.Body className="pb-0">
              <h5 className="mb-3">
                <i className="bi bi-list-check text-primary me-2"></i>
                Solicitudes de certificado
            </h5>
            </Card.Body>

            {loading ? (
              <Card.Body className="text-center py-5">
                <Spinner animation="border" />
                <p className="mt-3">Cargando…</p>
              </Card.Body>
            ) : certificados.length === 0 ? (
              <Card.Body>
                <p className="text-muted text-center py-4">
                  Todavía no ha solicitado ningún certificado.
                </p>
              </Card.Body>
            ) : (
              <Card.Body className="p-1 pt-0">
                <Table hover responsive className="align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Nº Registro</th>
                      <th>Tipo</th>
                      <th>Fecha de solicitud</th>
                      <th>Estado</th>
                      <th>Descarga</th>
                    </tr>
                  </thead>
                  <tbody>
                    {certificados.map((cert) => (
                      <tr key={cert.SolicitudID}>
                        <td><code className="text-primary fw-bold">{cert.NumeroRegistro || "—"}</code></td>
                        <td className="small">{TIPOS_CERTIFICADO[cert.TipoCertificado] || cert.TipoCertificado}</td>
                        <td>
                          <small>
                            {cert.FechaSolicitud
                              ? new Date(cert.FechaSolicitud).toLocaleDateString("es-ES", {
                                  year: "numeric", month: "long", day: "numeric",
                                })
                              : "—"}
                          </small>
                        </td>
                        <td>{renderEstado(cert)}</td>
                        <td>
                          {cert.FirmadoAdmin ? (
                            <Button
                              size="sm"
                              variant="outline-success"
                              onClick={() => descargar(cert.DocumentoID, cert.NombreDocumento)}
                              disabled={descargando === cert.DocumentoID}
                            >
                              <i className="bi bi-download me-1"></i>Descargar
                            </Button>
                          ) : (
                            <span className="text-muted small">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            )}
          </Card>
        </Container>
      </main>
    </div>
  );
};

export default MisCertificados;
